import { difference } from 'es-toolkit';
import type {
  AccountWithThresholds,
  CalculatedThresholdsResult,
  JsonAccountThreshold,
} from 'src/store/shared/threshold/types';
import type { AccountView } from '@skynet/data-shared-accounts';

/**
 * Normalises an `UpsertAccountThreshold` / `DeleteAccountThreshold` action payload into the
 * entry the threshold slice keeps as `updateData` and commits once the server acks the command.
 *
 * The Add Threshold form's `InputNumber` runs in string mode, so `threshold` arrives as a numeric
 * string (e.g. `"100"`) despite its `number` type; delete payloads carry no threshold at all.
 * Unparseable or missing values fall back to `0`.
 */
export function toThresholdUpdateData(payload: Record<string, unknown>): JsonAccountThreshold {
  const threshold =
    typeof payload.threshold === 'string' ? Number(payload.threshold) : payload.threshold;
  return {
    accountID: typeof payload.accountID === 'string' ? payload.accountID : '',
    token: typeof payload.token === 'string' ? payload.token : '',
    threshold: typeof threshold === 'number' && Number.isFinite(threshold) ? threshold : 0,
  };
}

export function getAccountWithThresholds(
  accounts: AccountView[] = [],
  thresholds: Record<string, JsonAccountThreshold[]> = {},
  tokens: Record<string, [string, string]> | undefined = {},
  calculation: Record<string, CalculatedThresholdsResult> | undefined = {},
): AccountWithThresholds[] {
  return accounts.map((account) => {
    const accountThresholds = thresholds[account.name] ?? [];
    const thresholdTokens = accountThresholds.map((threshold) => threshold.token);
    const accountTokens = difference(tokens[account.name] ?? [], thresholdTokens);

    let calculatedThresholdsByAccount;

    const recommendationUseCalculate = calculation[account.name];

    if (recommendationUseCalculate) {
      const { coinsUsdtRequired, usdtRequired } = recommendationUseCalculate;
      calculatedThresholdsByAccount = [...accountTokens, ...thresholdTokens].map((token) => ({
        token: `${token} recommended`,
        threshold: token === 'USDT' ? usdtRequired : coinsUsdtRequired,
        accountID: account.name,
      }));
    }

    return {
      ...account,
      thresholds: accountThresholds,
      availableTokens: accountTokens,
      thresholdsRecommended: calculatedThresholdsByAccount,
    };
  });
}
